/**
 * Users Tab Component (BStock)
 *
 * Gere les acces utilisateurs au module BStock sur l'etablissement actif.
 * Pilote uniquement la colonne `bstockRole` (cf US-MAC.8) — pour les acces
 * BStaff/BDisplay, ouvrir la page utilisateurs du module concerne via
 * l'AppSwitcher.
 */

import { useState, useMemo } from 'react';
import { Plus, Search, Info, MoreHorizontal, RefreshCw, UserX } from 'lucide-react';
import { UserRole, ActivationStatus } from '@bstock/shared';
import type { UserListItem } from '@bstock/shared';
import { Button, Input, Skeleton, Badge, Select, Dropdown, DropdownItem, DropdownDivider } from '@/components/ui';
import { useUsers, useDeleteUser, useResendActivation, useUpdateModuleRoles } from '../../../users/hooks/useUsers';
import { PermissionsDrawer, UserFormModal } from '../../../users/components';
import { useAuth } from '@/context/AuthContext';
import { useFormatDate } from '@/hooks';

const NO_ACCESS = '__NO_ACCESS__';
type RoleOrNoAccess = UserRole | typeof NO_ACCESS;

const roleBadgeColors: Record<UserRole, 'info' | 'success' | 'warning' | 'neutral'> = {
  [UserRole.OWNER]: 'warning',
  [UserRole.ADMIN]: 'info',
  [UserRole.MANAGER]: 'info',
  [UserRole.OPERATOR]: 'success',
  [UserRole.VIEWER]: 'neutral',
};

const roleLabels: Record<UserRole, string> = {
  [UserRole.OWNER]: 'Proprietaire',
  [UserRole.ADMIN]: 'Administrateur',
  [UserRole.MANAGER]: 'Responsable',
  [UserRole.OPERATOR]: 'Operateur',
  [UserRole.VIEWER]: 'Consultation',
};

const statusConfig = {
  [ActivationStatus.ACTIVE]: { color: 'success' as const, label: 'Actif' },
  [ActivationStatus.INACTIVE]: { color: 'neutral' as const, label: 'Inactif' },
  [ActivationStatus.PENDING]: { color: 'warning' as const, label: 'En attente' },
};

export function UsersTab() {
  const { user: currentUser } = useAuth();
  const { formatDateShort } = useFormatDate();

  const formatRelativeDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDateShort(dateStr);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accessFilter, setAccessFilter] = useState<'' | 'with-access' | 'no-access'>('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);

  const deleteMutation = useDeleteUser();
  const resendMutation = useResendActivation();
  const updateModuleRolesMutation = useUpdateModuleRoles();

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: usersData, isLoading } = useUsers({
    search: debouncedSearch || undefined,
    page: 1,
    limit: 100,
  });

  const usersAll = usersData?.items ?? [];
  const users = useMemo(() => {
    if (accessFilter === 'with-access') return usersAll.filter((u) => u.bstockRole !== null);
    if (accessFilter === 'no-access') return usersAll.filter((u) => u.bstockRole === null);
    return usersAll;
  }, [usersAll, accessFilter]);

  const currentUserRole = (currentUser?.currentBrand.bstockRole ?? currentUser?.currentBrand.role) as UserRole;
  const isOwner = currentUserRole === UserRole.OWNER;
  const brandId = currentUser?.currentBrand.id;

  const accessFilterOptions = [
    { value: '', label: 'Tous les utilisateurs' },
    { value: 'with-access', label: 'Avec acces BStock' },
    { value: 'no-access', label: 'Aucun acces BStock' },
  ];

  // Helper functions for user actions
  const canResendActivation = (user: UserListItem) =>
    user.activationStatus === ActivationStatus.PENDING;

  const canChangeRole = (user: UserListItem) => {
    const isSelf = user.id === currentUser?.id;
    const targetIsOwner = user.bstockRole === UserRole.OWNER;
    return !isSelf && (isOwner || !targetIsOwner);
  };

  const canDelete = (user: UserListItem) => {
    const isSelf = user.id === currentUser?.id;
    return isOwner && !isSelf;
  };

  const hasActions = (user: UserListItem) =>
    canResendActivation(user) || canChangeRole(user) || canDelete(user);

  const getAvailableRoles = (user: UserListItem): RoleOrNoAccess[] => {
    const roles: UserRole[] = isOwner
      ? [UserRole.VIEWER, UserRole.OPERATOR, UserRole.MANAGER, UserRole.ADMIN, UserRole.OWNER]
      : [UserRole.VIEWER, UserRole.OPERATOR, UserRole.MANAGER, UserRole.ADMIN];

    return [...roles.filter((role) => role !== user.bstockRole), NO_ACCESS];
  };

  const handleResendActivation = (user: UserListItem) => {
    resendMutation.mutate(user.id);
  };

  const handleChangeRole = (userId: string, role: RoleOrNoAccess) => {
    if (!brandId) return;
    updateModuleRolesMutation.mutate({
      userId,
      brandId,
      input: { bstockRole: role === NO_ACCESS ? null : role },
    });
  };

  const handleDelete = (user: UserListItem) => {
    if (confirm(`Supprimer l'utilisateur ${user.firstName} ${user.lastName} ?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="pl-10"
            />
          </div>

          <Select
            value={accessFilter}
            onChange={(value) => setAccessFilter(value as '' | 'with-access' | 'no-access')}
            options={accessFilterOptions}
            className="w-56"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setIsPermissionsDrawerOpen(true)}
          >
            <Info className="w-4 h-4" />
            Voir les permissions
          </Button>

          <Button variant="primary" onClick={() => setIsFormModalOpen(true)}>
            <Plus className="w-4 h-4" />
            Nouvel utilisateur
          </Button>
        </div>
      </div>

      <div className="text-sm text-neutral-500">
        Cette page gere uniquement les acces <strong>BStock</strong> sur l&apos;etablissement
        actif. Pour les acces BStaff ou BDisplay, ouvrez la page utilisateurs du
        module concerne via l&apos;AppSwitcher.
      </div>

      {/* Users Table */}
      {isLoading ? (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center">
          <p className="text-neutral-500">
            {searchQuery || accessFilter ? 'Aucun utilisateur trouve' : 'Aucun utilisateur'}
          </p>
          {!searchQuery && !accessFilter && (
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => setIsFormModalOpen(true)}
            >
              <Plus className="w-4 h-4" />
              Inviter un utilisateur
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-neutral-600">
                  Nom
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-neutral-600">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-neutral-600">
                  Role BStock
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-neutral-600">
                  Statut
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-neutral-600">
                  Derniere connexion
                </th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {users.map((user) => {
                const isSelf = user.id === currentUser?.id;
                const statusCfg = statusConfig[user.activationStatus];
                const moduleRole = user.bstockRole;

                return (
                  <tr
                    key={user.id}
                    className={isSelf ? 'bg-primary-50/30' : 'hover:bg-neutral-50'}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-dark-500">
                          {user.firstName} {user.lastName}
                        </span>
                        {isSelf && (
                          <span className="text-xs text-primary-600 font-medium">
                            (vous)
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {user.email}
                    </td>
                    <td className="px-4 py-3">
                      {moduleRole === null ? (
                        <Badge variant="neutral">Aucun acces</Badge>
                      ) : (
                        <Badge variant={roleBadgeColors[moduleRole]}>
                          {roleLabels[moduleRole]}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusCfg.color}>
                        {statusCfg.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500">
                      {formatRelativeDate(user.lastLoginAt)}
                    </td>
                    <td className="px-4 py-3">
                      {hasActions(user) && (
                        <Dropdown
                          trigger={
                            <button className="p-1 rounded hover:bg-neutral-100">
                              <MoreHorizontal className="w-4 h-4 text-neutral-400" />
                            </button>
                          }
                        >
                          {canResendActivation(user) && (
                            <DropdownItem
                              icon={<RefreshCw className="w-4 h-4" />}
                              onClick={() => handleResendActivation(user)}
                            >
                              Renvoyer l&apos;invitation
                            </DropdownItem>
                          )}

                          {canChangeRole(user) && (
                            <>
                              <DropdownDivider />
                              <div className="px-3 py-1 text-xs font-medium text-neutral-400 uppercase">
                                Changer le role BStock
                              </div>
                              {getAvailableRoles(user).map((role) => (
                                <DropdownItem
                                  key={role}
                                  onClick={() => handleChangeRole(user.id, role)}
                                  className={role === NO_ACCESS ? 'text-neutral-500' : undefined}
                                >
                                  {role === NO_ACCESS ? 'Aucun acces' : roleLabels[role]}
                                </DropdownItem>
                              ))}
                            </>
                          )}

                          {canDelete(user) && (
                            <>
                              <DropdownDivider />
                              <DropdownItem
                                icon={<UserX className="w-4 h-4" />}
                                onClick={() => handleDelete(user)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                Supprimer
                              </DropdownItem>
                            </>
                          )}
                        </Dropdown>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals and Drawers */}
      <UserFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
      />

      <PermissionsDrawer
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
      />
    </div>
  );
}
